import { cn, dayjs, getDiffExpirationDate } from '@/utils';
import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { DatePicker, TooltipProps } from 'antd';
import { DatePickerProps } from 'antd/es/date-picker';

import type { Dayjs } from 'dayjs';

import Tooltip from '../tooltip';
import {
	createAllowedDateResolver,
	relaxDisabledDate,
} from './segmented-date.util';

export type DatePickerData = {
	date: any;
	dateString: string | string[];
	name: string;
	outputFormat: string;
};

type PanelMode = NonNullable<DatePickerProps['mode']>;

type Segment = {
	mode: Extract<PanelMode, 'year' | 'month' | 'date'>;
	start: number;
	length: number;
};

export type DatePickerFormProps = Omit<
	DatePickerProps,
	'value' | 'onChange'
> & {
	name?: string;
	className?: string;
	onChange?: (data: DatePickerData) => void;
	value: string;
	boxStyle?: React.CSSProperties;
	containerClassName?: string;
	inputClassName?: string;
	format?: string;
	outputFormat?: string;
	showDifferenceDate?: boolean;
	inputReadOnly?: boolean;
	/**
	 * Bật chế độ "chọn theo từng vùng" cho các format canh chữ kiểu
	 * `YYYY      MM      DD`: click vùng YYYY mở panel năm, vùng MM mở panel
	 * tháng, vùng DD mở panel ngày. Chọn xong ở cấp nào thì commit ngay ở cấp đó
	 * (giữ nguyên các phần còn lại của ngày hiện tại).
	 *
	 * `YYYY      MM      DD` 형태의 커스텀 포맷에서 "영역별 선택" 모드를 켭니다.
	 * YYYY 영역을 클릭하면 연도 패널, MM 영역은 월 패널, DD 영역은 일 패널이 열리며,
	 * 선택한 단계에서 바로 반영됩니다(나머지 부분은 기존 값을 유지).
	 */
	segmentedPanel?: boolean;
	/**
	 * Nội dung tooltip hướng dẫn nhập, chỉ hiện khi `segmentedPanel` bật.
	 * Truyền `null` để tắt tooltip cho riêng field đó.
	 *
	 * 입력 안내 툴팁 내용이며 `segmentedPanel`이 켜진 경우에만 표시됩니다.
	 * 해당 필드만 툴팁을 끄려면 `null`을 전달하세요.
	 */
	guideTooltip?: React.ReactNode;
	/**
	 * Tuỳ chỉnh thêm cho Tooltip (placement, overlayClassName...).
	 *
	 * 툴팁 추가 설정(placement, overlayClassName 등).
	 */
	guideTooltipProps?: Omit<TooltipProps, 'title' | 'children'>;
};

export const SEGMENTED_PANEL_GUIDE =
	'년, 월, 일을 직접 입력하시려면\n먼저 캘린더에서 날짜를 선택 후 수정해 주세요.';

// Ưu tiên token dài trước để 'YYYY' không bị 'YY' bắt mất.
// 'YYYY'가 'YY'로 잘못 인식되지 않도록 긴 토큰을 먼저 확인합니다.
const SEGMENT_TOKENS: Array<{ tokens: string[]; mode: Segment['mode'] }> = [
	{ tokens: ['YYYY', 'YY'], mode: 'year' },
	{ tokens: ['MM', 'M'], mode: 'month' },
	{ tokens: ['DD', 'D'], mode: 'date' },
];

const BASE_PARSE_FORMATS = [
	'YYYY-MM-DD',
	'YYYY/MM/DD',
	'YYYY.MM.DD',
	'YYYY MM DD',
	'YYYYMMDD',
	'YYYY-M-D',
	'YYYY/M/D',
	'YYYY.M.D',
	'YYYY M D',
	'YY-MM-DD',
	'YYMMDD',
];

/**
 * Ghép năm (hoặc năm + tháng) mới vào ngày hiện tại, chặn tràn tháng
 * (31/01 + đổi sang tháng 2 phải ra 28/02 chứ không nhảy sang tháng 3).
 *
 * 선택한 연도(또는 연도 + 월)를 기존 날짜에 합치며 월 넘침을 막습니다
 * (1월 31일에서 2월로 바꾸면 3월이 아니라 2월 28일이 되어야 함).
 */
const mergeUnit = (base: Dayjs, picked: Dayjs, unit: 'year' | 'month') => {
	const year = picked.year();
	const month = unit === 'year' ? base.month() : picked.month();
	const firstDay = dayjs(
		`${year}-${String(month + 1).padStart(2, '0')}-01`,
		'YYYY-MM-DD'
	);

	return firstDay.date(Math.min(base.date(), firstDay.daysInMonth()));
};

const DatePickerForm = ({
	className,
	onChange,
	value,
	boxStyle,
	containerClassName,
	name = '',
	format,
	outputFormat = 'YYYY-MM-DD',
	showDifferenceDate = false,
	inputReadOnly = false,
	segmentedPanel = false,
	guideTooltip = SEGMENTED_PANEL_GUIDE,
	guideTooltipProps,
	disabledDate,
	onPanelChange,
	onOpenChange,
	...rest
}: DatePickerFormProps) => {
	const DATE_FORMAT = format || 'YYYY-MM-DD';

	const containerRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [hovered, setHovered] = useState(false);
	const [mode, setMode] = useState<PanelMode>('date');

	const showGuideTooltip = segmentedPanel && !!guideTooltip;

	// Định dạng chuẩn dùng để parse khi người dùng gõ tay (Năm-Tháng-Ngày).
	// Phần tử đầu (DATE_FORMAT) dùng để HIỂN THỊ (giữ template canh chữ),
	// toàn bộ mảng được antd dùng để PARSE input -> gõ format nào cũng nhận.
	// Với format nhiều khoảng trắng, thêm 2 biến thể (gộp 1 space / bỏ hết
	// space) để user gõ tay không phải đếm khoảng trắng.
	//
	// 직접 입력(연-월-일)을 파싱할 때 사용하는 포맷 목록입니다.
	// 첫 번째 항목(DATE_FORMAT)은 화면 표시용(칸 맞춤 템플릿 유지)이고,
	// 배열 전체는 antd가 입력값을 파싱할 때 사용하므로 어떤 포맷으로 입력해도 인식됩니다.
	// 공백이 많은 포맷은 변형 2가지(공백 1칸으로 축소 / 공백 제거)를 추가해
	// 사용자가 공백 개수를 맞추지 않아도 되게 합니다.
	const dateFormats = useMemo(() => {
		const variants = [
			DATE_FORMAT,
			DATE_FORMAT.replace(/\s+/g, ' ').trim(),
			DATE_FORMAT.replace(/\s+/g, ''),
			...BASE_PARSE_FORMATS,
		];

		return Array.from(new Set(variants.filter(Boolean)));
	}, [DATE_FORMAT]);

	// Vị trí của từng vùng YYYY / MM / DD trong chuỗi format. Do token có độ dài
	// đúng bằng số ký tự hiển thị nên chỉ số trong format map 1:1 với chỉ số
	// con trỏ trong input.
	//
	// 포맷 문자열에서 YYYY / MM / DD 영역의 위치입니다. 토큰 길이가 실제 표시되는
	// 글자 수와 같으므로 포맷의 인덱스와 입력창 커서 위치가 1:1로 대응됩니다.
	const segments = useMemo<Segment[]>(() => {
		if (!segmentedPanel) return [];

		return SEGMENT_TOKENS.reduce<Segment[]>((acc, { tokens, mode: unit }) => {
			for (const token of tokens) {
				const start = DATE_FORMAT.indexOf(token);

				if (start >= 0) {
					acc.push({ mode: unit, start, length: token.length });
					break;
				}
			}

			return acc;
		}, []);
	}, [DATE_FORMAT, segmentedPanel]);

	// Con trỏ nằm gần tâm vùng nào nhất thì mở panel của vùng đó (khoảng trắng
	// giữa 2 vùng được chia đôi). Input rỗng -> con trỏ ở 0 -> mở panel năm.
	//
	// 커서와 가장 가까운 영역의 패널을 엽니다(두 영역 사이 공백은 절반씩 나눠 판정).
	// 입력값이 비어 있으면 커서가 0이므로 연도 패널이 열립니다.
	const syncModeFromCaret = useCallback(() => {
		if (!segments.length) return;

		const input = containerRef.current?.querySelector('input');
		const caret = input?.selectionStart ?? 0;

		const nearest = segments.reduce((best, segment) => {
			const distance = Math.abs(caret - (segment.start + segment.length / 2));
			const bestDistance = Math.abs(caret - (best.start + best.length / 2));

			return distance < bestDistance ? segment : best;
		}, segments[0]);

		setMode(nearest.mode);
	}, [segments]);

	const disabledDateInner = useMemo(
		() => relaxDisabledDate(disabledDate, segmentedPanel),
		[disabledDate, segmentedPanel]
	);

	const resolveAllowedDate = useMemo(
		() => createAllowedDateResolver(disabledDateInner as any),
		[disabledDateInner]
	);

	const onChangeInner = useCallback(
		(date: any, dateString: string | string[]) => {
			onChange?.({ date, dateString, name, outputFormat });
		},
		[onChange, name, outputFormat]
	);

	const commit = useCallback(
		(date: Dayjs) => {
			onChangeInner(date, date.format(DATE_FORMAT));
			setOpen(false);
		},
		[onChangeInner, DATE_FORMAT]
	);

	const onPanelChangeInner = useCallback(
		(panelDate: any, nextMode: PanelMode) => {
			if (segmentedPanel) {
				const current = value ? dayjs(value, outputFormat) : null;
				const canMerge = !!current?.isValid();

				// Drill-down năm -> tháng nghĩa là user vừa click 1 ô năm.
				// Nếu đã có sẵn giá trị thì chỉ thay phần năm rồi đóng luôn,
				// khỏi bắt user chọn lại tháng/ngày.
				//
				// 연도 -> 월로 패널이 내려갔다는 것은 사용자가 연도 셀을 클릭했다는 뜻입니다.
				// 이미 값이 있으면 연도만 교체하고 바로 닫아, 월/일을 다시 고르지 않게 합니다.
				const unit: 'year' | 'month' | null =
					mode === 'year' && nextMode === 'month'
						? 'year'
						: mode === 'month' && nextMode === 'date'
							? 'month'
							: null;

				if (canMerge && unit) {
					const merged = resolveAllowedDate(
						mergeUnit(current as Dayjs, panelDate, unit)
					);

					if (merged) {
						commit(merged);
						onPanelChange?.(panelDate, nextMode);
						return;
					}
				}

				setMode(nextMode);
			}

			onPanelChange?.(panelDate, nextMode);
		},
		[
			segmentedPanel,
			value,
			outputFormat,
			mode,
			resolveAllowedDate,
			commit,
			onPanelChange,
		]
	);

	const onOpenChangeInner = useCallback(
		(nextOpen: boolean) => {
			setOpen(nextOpen);

			// Con trỏ chỉ được đặt sau khi antd xử lý xong click nên phải đọc ở
			// tick kế tiếp.
			//
			// 커서 위치는 antd가 클릭을 처리한 뒤에 잡히므로 다음 tick에서 읽어야 합니다.
			if (nextOpen) setTimeout(syncModeFromCaret, 0);

			onOpenChange?.(nextOpen);
		},
		[syncModeFromCaret, onOpenChange]
	);

	const onClickContainer = useCallback(() => {
		if (segmentedPanel) syncModeFromCaret();
	}, [segmentedPanel, syncModeFromCaret]);

	const diffDate = useMemo(() => {
		return getDiffExpirationDate(value);
	}, [value]);

	const showDiffDate = useMemo(() => {
		return showDifferenceDate && diffDate > 0;
	}, [showDifferenceDate, diffDate]);

	const segmentedProps = segmentedPanel
		? {
				open,
				mode,
				onOpenChange: onOpenChangeInner,
				onPanelChange: onPanelChangeInner,
			}
		: { onOpenChange, onPanelChange };

	const content = (
		<div
			ref={containerRef}
			className={containerClassName}
			style={boxStyle}
			onClick={onClickContainer}
		>
			{showDiffDate && (
				<div className="text-[12px] leading-normal">{`[D-${diffDate}]`}</div>
			)}
			<DatePicker
				onChange={onChangeInner}
				className={cn(
					'bg-transparent font-light hover:bg-transparent focus:bg-transparent focus-within:bg-transparent !border-none focus:!outline-none !shadow-none p-0 date-input',
					className,
					showDiffDate && '[&>.ant-picker-input>input]:!top-[-6px]'
				)}
				placeholder=""
				suffixIcon
				inputReadOnly={inputReadOnly}
				value={value ? dayjs(value, outputFormat) : null}
				format={dateFormats}
				disabledDate={disabledDateInner}
				{...rest}
				{...segmentedProps}
			/>
		</div>
	);

	if (!showGuideTooltip) return content;

	return (
		<Tooltip
			title={guideTooltip}
			// Ẩn tooltip khi panel đang mở để không che lịch.
			//
			// 패널이 열려 있을 때는 달력을 가리지 않도록 툴팁을 숨깁니다.
			open={hovered && !open}
			onOpenChange={setHovered}
			placement="top"
			mouseEnterDelay={0.3}
			{...guideTooltipProps}
			// Nới bề ngang tooltip thêm 20px so với mặc định của antd (250px).
			//
			// antd 기본값(250px)보다 툴팁 너비를 20px 넓힙니다.
			overlayClassName={cn(
				'!max-w-[270px]',
				guideTooltipProps?.overlayClassName
			)}
		>
			{content}
		</Tooltip>
	);
};

export default memo(DatePickerForm);
